import React, { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useBreakpoints } from "@qgiv/core-react";
import { getHasPreviouslyAcceptedOrCurrentlyIsOnReminderEmail } from "@qgiv/donation-form";
import {
    selectAppModalOpen,
    selectContentToDisplay,
    updateContentToDisplay,
} from "../../../redux/slices/appSettingsSlice";
import { initMutationObserver } from "../../MutationObservers/initMutationObservers";
import { sendPostMessage } from "../../PostMessage/postMessage";
import { ModalContentReplacerEnums } from "../../common/ModalContentReplacer";
import { selectPreventRefreshOnClose } from "../../../redux/slices/urlShortcutsSlice";
import {
    setAbandonedGiftDataClosedElementFromApp,
    selectAbandonedGiftClosedElementFromApp,
    setHasBeenPromptedToReturnToGift,
} from "../../../redux/slices/abandonedGiftSlice";
import { selectAllDonationSettings } from "../../../redux/slices/donationSettingsSlice";
import { selectSubtotalGift } from "../../../redux/slices/donationDetailsSlice";
import { selectHasActiveFulfillment } from "../../../redux/slices/promiseTransactionSlice";
import { selectIsTextToDonate } from "../../../redux/slices/smsDataSlice";
import DonationFormModal from "./DonationFormModal";

const ConnectedDonationFromModal = () => {
    const dispatch = useDispatch();
    const appModalOpen = useSelector(selectAppModalOpen);
    const selectedContentToDisplay = useSelector(selectContentToDisplay);
    const displayCloseButton =
        selectedContentToDisplay !==
        ModalContentReplacerEnums.UPGRADE_RECURRING_UI;

    const donationSettings = useSelector(selectAllDonationSettings);
    const { enableAbandonedGifts = false, enableReminderEmails = false } =
        donationSettings;
    const abandonedGiftClosedElementFromApp = useSelector(
        selectAbandonedGiftClosedElementFromApp,
    );
    const subtotal = useSelector(selectSubtotalGift);
    const hasActiveFulfillment = useSelector(selectHasActiveFulfillment);
    const isTextToDonate = useSelector(selectIsTextToDonate);

    useEffect(() => {
        if (!abandonedGiftClosedElementFromApp && enableAbandonedGifts) {
            dispatch(setAbandonedGiftDataClosedElementFromApp(true));
            sendPostMessage("closeElementFromApp");
        }
    }, [abandonedGiftClosedElementFromApp, enableAbandonedGifts, dispatch]);

    // Currently, we are performing a refresh when closing the donation form modal.
    // This is to handle session in the legacy landing page that may change if
    // a donor logs in while on the form in the modal. This is a workaround
    // until we figure out how to listen to a postmessage on the legacy page
    // to update session. There are certain routes where we do not want to allow
    // this behavior, such as /donate routes, which launch the modal on page load.
    // If we are a route like that, the BE is sending us a flag in urlShortcuts to
    // prevent refreshing the parent window on modal close, which is handled in
    // embed.js
    const preventRefreshOnClose = useSelector(selectPreventRefreshOnClose);

    // This flag is for when the close button should not close the modal, but return
    // the donor to the default content in the modal. This is currently only being used
    // by the recipient search UI.

    const sendCloseFullScreenModalMessage = useCallback(() => {
        sendPostMessage("closeFullScreenModal", preventRefreshOnClose);
    }, [preventRefreshOnClose]);

    // There will be a use case for non-abandoned gifts enabled forms
    // to display the Exit Intent UI if they have reached a certain spot
    // in the Form. Event Registration has this spot as having entered the email
    // address on the Details Page. We should probably maintain that as the
    // reason to pop the Exit Intent UI. For now, we will use a hardcoded false
    // value to allow non-abandoned gifts forms to close modal.
    const hasEnteredEnoughDataToDisplayExitIntent = false;

    const selectedContentIsExitIntentContent =
        selectedContentToDisplay === ModalContentReplacerEnums.EXIT_INTENT_UI;

    const hasAbandonedGiftReminderEmailsEnabled =
        enableAbandonedGifts && enableReminderEmails;

    const hasPreviouslyAcceptedOrCurrentlyOnReminderEmail =
        getHasPreviouslyAcceptedOrCurrentlyIsOnReminderEmail();

    const hasValidAmount = subtotal > 0;

    const displayExitIntentUIAbandonedGifts =
        !hasActiveFulfillment &&
        !isTextToDonate &&
        !selectedContentIsExitIntentContent &&
        !hasPreviouslyAcceptedOrCurrentlyOnReminderEmail &&
        hasValidAmount &&
        hasAbandonedGiftReminderEmailsEnabled;

    const displayExitIntentUI =
        !selectedContentIsExitIntentContent &&
        (displayExitIntentUIAbandonedGifts ||
            hasEnteredEnoughDataToDisplayExitIntent);

    const displayDefaultContent =
        selectedContentToDisplay ===
        ModalContentReplacerEnums.RECIPIENT_SEARCH_UI;

    const handleCloseDonationFormModal = () => {
        if (displayExitIntentUI) {
            dispatch(
                updateContentToDisplay(
                    ModalContentReplacerEnums.EXIT_INTENT_UI,
                ),
            );
            if (displayExitIntentUIAbandonedGifts) {
                // we need to update tracking data state
                dispatch(setHasBeenPromptedToReturnToGift(true));
            }
            return;
        }
        if (displayDefaultContent) {
            dispatch(
                updateContentToDisplay(
                    ModalContentReplacerEnums.DEFAULT_CONTENT,
                ),
            );
            return;
        }
        sendCloseFullScreenModalMessage();
    };

    const { isMediumScreen } = useBreakpoints();
    // flag for setting the embed container's height to the parent window's height
    // which is used in the embed logic. Also overrides styles for the modal2__content-wrapper element.
    const hardModalFullHeight =
        isMediumScreen &&
        selectedContentToDisplay ===
            ModalContentReplacerEnums.RECIPIENT_SEARCH_UI;

    useEffect(() => {
        const observer = initMutationObserver();
        return observer?.disconnect;
    }, []);

    useEffect(() => {
        sendPostMessage("setHardFullHeightOnModal", {
            hardModalFullHeight,
        });
    }, [hardModalFullHeight]);

    const donationFormModalProps = {
        appModalOpen,
        displayCloseButton,
        handleCloseDonationFormModal,
        hardModalFullHeight,
    };
    return <DonationFormModal {...donationFormModalProps} />;
};

export default ConnectedDonationFromModal;
